import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './lib/auth';
import { ToastProvider } from './components/toast';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout';
import { ReminderScheduler } from './components/ReminderScheduler';
import { RecurrenceCatchUp } from './components/RecurrenceCatchUp';
import { Login } from './pages/Login';
import { Dashboard, DashboardTaskPanelRoute } from './pages/Dashboard';
import { Projects } from './pages/Projects';
import { ProjectEditor } from './pages/ProjectEditor';
import {
  ProjectDetail,
  ProjectEdit,
  ProjectOverview,
  ProjectSectionFallback,
  ProjectWhiteboard,
} from './pages/ProjectDetail';
import { ProjectTasks, TaskPanelRoute } from './pages/ProjectTasks';
import { AllTasks, AllTasksPanelRoute } from './pages/AllTasks';
import { DailyWins } from './pages/DailyWins';
import { Assignees } from './pages/Assignees';
import { AssigneeEditor } from './pages/AssigneeEditor';

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          {/* Beside the routes rather than inside one. A task reminder has to be
              able to arrive while you are looking at any page, or none of them --
              a scheduler mounted under a route would go quiet the moment you
              navigated away from it. Renders nothing. */}
          <ReminderScheduler />
          {/* Beside it for the same reason: a repeating task's due date can
              arrive while any page is open. Also renders nothing. */}
          <RecurrenceCatchUp />
          <Routes>
            <Route path="/login" element={<Login />} />

            {/* A pathless layout route, so the auth gate and chrome are declared
                once rather than repeated around every protected page. */}
            <Route
              element={
                <ProtectedRoute>
                  <Layout>
                    <Outlet />
                  </Layout>
                </ProtectedRoute>
              }
            >
              {/* The dashboard, with the task panel over it. Same nested shape
                  the two task lists use, and for the same reason: the page is
                  the route's own element rather than an index route, because
                  an index route unmounts the moment `:taskId` matches and the
                  panel is an overlay over the page. Singular `task/`, so it
                  cannot be read as the `/tasks` list. */}
              <Route path="/" element={<Dashboard />}>
                <Route path="task/:taskId" element={<DashboardTaskPanelRoute />} />
              </Route>
              {/* The dashboard's day list, read back. Reached from the card
                  that owns it rather than from the sidebar: the nav answers
                  "where am I", and this is a leaf of the dashboard rather than
                  a fifth place to be. */}
              <Route path="/wins" element={<DailyWins />} />

              {/* `new` is a static segment so it wins over `:id`, the same
                  arrangement /projects/new already relies on. */}
              <Route path="/assignees" element={<Assignees />} />
              <Route path="/assignees/new" element={<AssigneeEditor />} />
              <Route path="/assignees/:id" element={<AssigneeEditor />} />

              {/* Every task, whatever project it is in. Same nested-panel
                  shape the project tab uses: the list is the route's own
                  element rather than an index route, because an index route
                  unmounts the moment `:taskId` matches and the panel is an
                  overlay over the list. */}
              <Route path="/tasks" element={<AllTasks />}>
                <Route path=":taskId" element={<AllTasksPanelRoute />} />
              </Route>

              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/new" element={<ProjectEditor />} />

              {/* One project, with each section its own URL. `new` is a static
                  segment so it still wins over `:id`. */}
              <Route path="/projects/:id" element={<ProjectDetail />}>
                {/* The one place that decides which tab a project opens on.
                    Everything that links to a project points at `/projects/:id`
                    and lands here, so changing the default is this line alone. */}
                <Route index element={<Navigate to="tasks" replace />} />
                <Route path="overview" element={<ProjectOverview />} />
                {/* The list is this route's own element, not an index route:
                    an index route unmounts as soon as `:taskId` matches, and the
                    detail panel overlays the list rather than replacing it. */}
                <Route path="tasks" element={<ProjectTasks />}>
                  <Route path=":taskId" element={<TaskPanelRoute />} />
                </Route>
                <Route path="whiteboard" element={<ProjectWhiteboard />} />
                <Route path="edit" element={<ProjectEdit />} />
                <Route path="*" element={<ProjectSectionFallback />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
